import { UserEntity } from '../types/entity';
import { UserInDto } from '../types/inDto';
import { userWithPermissionsInDtoToUserInDto } from './toOutDto';
import {
  isUserWithPermissionsInDto,
  UserEditApiResponse,
} from '../types/userInDtoGuards';
import { UserRole } from 'shared/types/userRole';
import { isNonEmptyArray } from 'shared/utils/array';
import { compareObjectIds } from 'shared/utils/id/compare';
import {
  ProjectPermissionSnapshot,
  NormalizeAndPersistUserOptions,
} from './permissionsSnapshot';
import { getFirstUserInDtoOrThrow } from './userEditResponse';

export function isEditedUser(
  editedUserId: string | undefined,
  candidateUserId: string
): boolean {
  if (!editedUserId) {
    return false;
  }

  return compareObjectIds(
    editedUserId,
    candidateUserId,
    'isEditedUser'
  );
}

export function applySentProjectPermissionToUserInDto(
  userInDto: UserInDto,
  options?: NormalizeAndPersistUserOptions
): UserInDto {
  if (
    !options?.sentProjectPermission ||
    !isEditedUser(options.editedUserId, userInDto._id)
  ) {
    return userInDto;
  }

  return {
    ...userInDto,
    sites: options.sentProjectPermission.sites,
    processes: options.sentProjectPermission.processes,
    role: options.sentProjectPermission.role,
  };
}

export function userInDtoToEntity(user: UserInDto): UserEntity {
  return {
    _id: user._id,
    createdAt: user.createdAt,
    createdBy: user.createdBy,
    modifiedAt: user.modifiedAt,
    modifiedBy: user.modifiedBy,

    sites: user.sites ?? [],
    personalData: user.personalData,
    processes: user.processes ?? [],
    role: user.role ?? UserRole.standard,
    email: user.email,
  };
}

export function mergeUserInDtoIntoEntity(
  existing: UserEntity | undefined,
  userInDto: UserInDto,
  projectPermission?: ProjectPermissionSnapshot
): UserEntity {
  const incoming = userInDtoToEntity(userInDto);
  if (!existing) {
    return applyProjectPermissionSnapshot(incoming, projectPermission);
  }

  const merged: UserEntity = {
    ...existing,
    modifiedAt: incoming.modifiedAt ?? existing.modifiedAt,
    modifiedBy: incoming.modifiedBy ?? existing.modifiedBy,
    email: incoming.email || existing.email,
    personalData: isNonEmptyArray(incoming.personalData)
      ? incoming.personalData
      : existing.personalData,
    // Permission fields (role, sites, processes): always keep existing values here.
    // applyProjectPermissionSnapshot will overwrite them if projectPermission is provided.
    // This ensures that non-permission edits (label/phone) never touch permission fields,
    // and permission edits always go through the explicit projectPermission snapshot.
    role: existing.role ?? UserRole.standard,
    sites: existing.sites ?? [],
    processes: existing.processes ?? [],
  };

  return applyProjectPermissionSnapshot(merged, projectPermission);
}

function applyProjectPermissionSnapshot(
  entity: UserEntity,
  projectPermission?: ProjectPermissionSnapshot
): UserEntity {
  if (!projectPermission) {
    return entity;
  }

  return {
    ...entity,
    sites: projectPermission.sites,
    processes: projectPermission.processes,
    role: projectPermission.role,
  };
}

export function userEditApiResponsesToProjectUsers(
  organizationId: string,
  projectId: string,
  response: UserEditApiResponse | UserEditApiResponse[]
): UserInDto[] {
  const users = Array.isArray(response) ? response : [response];

  return users.map((user) =>
    isUserWithPermissionsInDto(user)
      ? userWithPermissionsInDtoToUserInDto(user, organizationId, projectId)
      : user
  );
}

export function toFlatUserInDtoOnlyResponse(
  response: UserEditApiResponse | UserEditApiResponse[]
): UserInDto | UserInDto[] {
  const users = Array.isArray(response) ? response : [response];
  const flatUsers = users.filter(
    (user): user is UserInDto => !isUserWithPermissionsInDto(user)
  );

  if (Array.isArray(response)) {
    return flatUsers;
  }

  return getFirstUserInDtoOrThrow(flatUsers, 'toFlatUserInDtoOnlyResponse');
}
